import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  Divider,
  ImageList,
  ImageListItem,
  Tooltip,
  Fade,
  Backdrop,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  SailingOutlined,
  CheckCircle,
  Cancel,
  Visibility,
  PhotoLibrary,
  Person,
  VerifiedUser,
  Security,
  Image as ImageIcon,
  BrokenImage,
  Close,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  RestartAlt,
  Collections,
  Edit,
  AttachMoney,
  VisibilityOff,
  Place,
} from '@mui/icons-material';
import type { PendingYacht } from '@/services/yachtApprovalService';
import { yachtApprovalService } from '@/services/yachtApprovalService';
import { YachtImageManagementDialog } from './YachtImageManagementDialog';
import { toast } from 'react-hot-toast';
import ManageYachtPricingDialog from '@/components/pricing/ManageYachtPricingDialog';
import type { YachtWithPricing } from '@/services/yachtDestinationPricingService';

// Currency configuration
const CURRENCY_CONFIG: Record<string, { symbol: string; name: string }> = {
  NGN: { symbol: '₦', name: 'Nigerian Naira' },
  USD: { symbol: '$', name: 'US Dollar' },
  EUR: { symbol: '€', name: 'Euro' },
  GBP: { symbol: '£', name: 'British Pound' },
};

const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_CONFIG[currencyCode]?.symbol || currencyCode;
};

interface YachtApprovalCardProps {
  yacht: PendingYacht;
  onApprove: (yachtId: string, notes?: string) => Promise<void>;
  onReject: (yachtId: string, reason: string) => Promise<void>;
  onPricingUpdate?: () => void;
  onYachtUpdate?: (yachtId: string, updates: Partial<PendingYacht>) => void;
}

export const YachtApprovalCard: React.FC<YachtApprovalCardProps> = ({
  yacht,
  onApprove,
  onReject,
  onPricingUpdate,
  onYachtUpdate
}) => {
  const [localIsActive, setLocalIsActive] = useState<boolean | undefined>(yacht.isActive);

  useEffect(() => {
    setLocalIsActive(yacht.isActive);
  }, [yacht.isActive]);

  useEffect(() => {
    // Yacht data tracked here
  }, [yacht]);

  // State for dialogs
  const [approveDialog, setApproveDialog] = useState<{ open: boolean; notes: string }>({
    open: false,
    notes: ''
  });
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; reason: string }>({
    open: false,
    reason: ''
  });
  const [revertDialog, setRevertDialog] = useState<{ open: boolean; notes: string }>({
    open: false,
    notes: ''
  });
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [imageViewer, setImageViewer] = useState({
    open: false,
    currentIndex: 0,
    zoom: 1,
    showMagnifier: false
  });
  const [toggleActiveDialog, setToggleActiveDialog] = useState({
    open: false,
    toggling: false
  });
  const [imageManagementDialog, setImageManagementDialog] = useState(false);
  const [destinationPricingDialog, setDestinationPricingDialog] = useState(false);

  // State for inline editing
  const [editingPricing, setEditingPricing] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [pricingForm, setPricingForm] = useState({
    charterRate: yacht.pricing?.charterRate || 0,
    weeklyRate: yacht.pricing?.weeklyRate || null,
    captainFee: yacht.pricing?.captainFee || null,
    crewFee: yacht.pricing?.crewFee || null,
    fuelSurcharge: yacht.pricing?.fuelSurcharge || null,
    cleaningFee: yacht.pricing?.cleaningFee || null,
    securityDeposit: yacht.pricing?.securityDeposit || null,
    currency: yacht.pricing?.currency || 'USD'
  });
  const [detailsForm, setDetailsForm] = useState({
    registrationNumber: yacht.yacht?.registrationNumber || '',
    homePort: yacht.yacht?.homePort || '',
    state: yacht.yacht?.state || '',
    city: yacht.yacht?.city || '',
    yearBuilt: yacht.yacht?.yearBuilt || '',
    guestCapacity: yacht.yacht?.guestCapacity || 0,
    crewCapacity: yacht.yacht?.crewCapacity || 0,
    cabins: yacht.yacht?.cabins || 0,
    bathrooms: yacht.yacht?.bathrooms || 0
  });

  const imageRef = useRef<HTMLImageElement>(null);
  const [magnifierPosition, setMagnifierPosition] = useState({ x: 0, y: 0 });

  // Helper to safely parse float values
  const safeParseFloat = (value: any, defaultValue: number | null = null): number | null => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Sync form state when yacht data changes
  useEffect(() => {
    const pricing = yacht.pricing as any;
    setPricingForm({
      charterRate: safeParseFloat(pricing?.full_day_rate || pricing?.charterRate, 0) || 0,
      weeklyRate: safeParseFloat(pricing?.weekly_rate, null),
      captainFee: safeParseFloat(pricing?.captain_fee, null),
      crewFee: safeParseFloat(pricing?.crew_fee, null),
      fuelSurcharge: safeParseFloat(pricing?.fuel_surcharge, null),
      cleaningFee: safeParseFloat(pricing?.cleaning_fee, null),
      securityDeposit: safeParseFloat(pricing?.security_deposit, null),
      currency: pricing?.currency || 'NGN'
    });
  }, [yacht.pricing, yacht.id]);

  useEffect(() => {
    const yachtData = yacht.yacht as any;
    setDetailsForm({
      registrationNumber: yachtData?.registration_number || yachtData?.registrationNumber || '',
      homePort: yachtData?.homeport || yachtData?.home_port || yachtData?.homePort || yachtData?.marina || '',
      state: yachtData?.state || '',
      city: yachtData?.city || '',
      yearBuilt: yachtData?.year?.toString() || yachtData?.yearBuilt || '',
      guestCapacity: yachtData?.guests ?? yachtData?.guest_capacity ?? yachtData?.guestCapacity ?? 0,
      crewCapacity: yachtData?.crew ?? yachtData?.crew_capacity ?? yachtData?.crewCapacity ?? 0,
      cabins: yachtData?.bedrooms ?? yachtData?.cabins ?? 0,
      bathrooms: yachtData?.bathrooms ?? 0
    });
  }, [yacht.yacht, yacht.id]);

  // Handlers
  const handleApprove = async () => {
    await onApprove(yacht.id, approveDialog.notes);
    setApproveDialog({ open: false, notes: '' });
  };

  const handleReject = async () => {
    if (!rejectDialog.reason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    await onReject(yacht.id, rejectDialog.reason);
    setRejectDialog({ open: false, reason: '' });
  };

  const handleRevert = async () => {
    try {
      const getAdminUserId = (): string => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            return user.id || user.userId || 'admin-default';
          } catch (e) {
          }
        }
        return 'admin-default';
      };

      const adminUserId = getAdminUserId();

      const result = await yachtApprovalService.revertToPending(
        yacht.id,
        adminUserId,
        revertDialog.notes || 'Reverting to pending for re-review'
      );

      if (result.success) {
        toast.success('Yacht reverted to pending review');
        setRevertDialog({ open: false, notes: '' });
        if (onPricingUpdate) onPricingUpdate(); // Refresh the list
      } else {
        toast.error(result.error || 'Failed to revert yacht');
      }
    } catch (error) {
      console.error('Error reverting yacht:', error);
      toast.error('Failed to revert yacht');
    }
  };

  const handleSavePricing = async () => {
    try {
      // Get admin user ID from localStorage
      let adminUserId = 'admin';
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          adminUserId = user.id;
        }
      } catch (e) {
        console.error('Error getting admin user ID:', e);
      }

      // Map frontend field names to backend field names
      const pricingData = {
        full_day_rate: pricingForm.charterRate,
        weekly_rate: pricingForm.weeklyRate,
        captain_fee: pricingForm.captainFee,
        crew_fee: pricingForm.crewFee,
        fuel_surcharge: pricingForm.fuelSurcharge,
        cleaning_fee: pricingForm.cleaningFee,
        security_deposit: pricingForm.securityDeposit,
        currency: pricingForm.currency,
        adminUserId
      };

      await yachtApprovalService.updateYachtPricing(yacht.id, pricingData as any);

      toast.success('Pricing updated successfully');
      setEditingPricing(false);
      onPricingUpdate?.();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        toast.error('Backend endpoint not implemented yet. Please notify the backend team to implement PUT /api/v1/admin/yachts/{id}/pricing');
      } else {
        toast.error('Failed to update pricing');
      }
      console.error('Error updating pricing:', error);
    }
  };

  const handleSaveDetails = async () => {
    try {
      // Get admin user ID from localStorage
      let adminUserId = 'admin';
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          adminUserId = user.id;
        }
      } catch (e) {
        console.error('Error getting admin user ID:', e);
      }

      // Map frontend field names to backend field names and ensure correct types
      const detailsData = {
        registration_number: detailsForm.registrationNumber || undefined,
        home_port: detailsForm.homePort || undefined,
        state: detailsForm.state || undefined,
        city: detailsForm.city || undefined,
        year: detailsForm.yearBuilt ? parseInt(detailsForm.yearBuilt) : undefined,
        guest_capacity: detailsForm.guestCapacity || undefined,
        crew_capacity: detailsForm.crewCapacity || undefined,
        cabins: detailsForm.cabins || undefined,
        bathrooms: detailsForm.bathrooms || undefined,
        adminUserId
      };

      await yachtApprovalService.updateYachtDetails(yacht.id, detailsData as any);

      toast.success('Yacht details updated successfully');
      setEditingDetails(false);
      onPricingUpdate?.();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        toast.error('Backend endpoint not implemented yet. Please notify the backend team to implement PUT /api/v1/admin/yachts/{id}/details');
      } else {
        toast.error('Failed to update yacht details');
      }
      console.error('Error updating details:', error);
    }
  };

  const handleImageClick = (index: number) => {
    setImageViewer({ open: true, currentIndex: index, zoom: 1, showMagnifier: false });
  };

  const handleNextImage = () => {
    setImageViewer(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % yacht.photos.items.length,
      zoom: 1
    }));
  };

  const handlePrevImage = () => {
    setImageViewer(prev => ({
      ...prev,
      currentIndex: prev.currentIndex === 0 ? yacht.photos.items.length - 1 : prev.currentIndex - 1,
      zoom: 1
    }));
  };

  const handleZoom = (delta: number) => {
    setImageViewer(prev => ({
      ...prev,
      zoom: Math.max(0.5, Math.min(5, prev.zoom + delta))
    }));
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !imageViewer.showMagnifier) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMagnifierPosition({ x, y });
  };

  const handleToggleActive = async () => {
    try {
      setToggleActiveDialog(prev => ({ ...prev, toggling: true }));
      const adminUserId = localStorage.getItem('adminUserId') || 'admin';

      const result = await yachtApprovalService.toggleYachtVisibility(
        yacht.id,
        !localIsActive,
        adminUserId
      );

      if (result.success) {
        // Get the new isActive value from the backend response
        const newIsActive = result.data?.isActive ?? result.data?.is_active ?? !localIsActive;

        setLocalIsActive(newIsActive);
        toast.success(`Yacht ${newIsActive ? 'shown to' : 'hidden from'} renters`);
        setToggleActiveDialog({ open: false, toggling: false });

        // Update parent state directly (like drivers do) instead of refetching
        if (onYachtUpdate) {
          onYachtUpdate(yacht.id, { isActive: newIsActive });
        } else {
          // Fallback to refetch if callback not provided
          onPricingUpdate?.();
        }
      } else {
        toast.error(result.error || 'Failed to toggle yacht visibility');
        setToggleActiveDialog(prev => ({ ...prev, toggling: false }));
      }
    } catch (error) {
      toast.error('Failed to toggle yacht visibility');
      setToggleActiveDialog(prev => ({ ...prev, toggling: false }));
    }
  };

  // Calculate completion percentage
  const calculateCompletion = () => {
    let total = 0;
    let completed = 0;

    const yachtData = yacht.yacht as any;
    const pricingData = yacht.pricing as any;

    // Essential fields - check both backend (snake_case) and frontend (camelCase) field names
    const essentialFields = [
      yachtData?.name,
      yachtData?.manufacturer,
      yachtData?.model,
      yachtData?.year || yachtData?.yearBuilt,
      yachtData?.yacht_type || yachtData?.type,
      yachtData?.length,
      yachtData?.homeport || yachtData?.home_port || yachtData?.homePort || yachtData?.marina,
      yachtData?.registration_number || yachtData?.registrationNumber,
      yachtData?.guests ?? yachtData?.guest_capacity ?? yachtData?.guestCapacity,
      yacht.host?.name,
      yacht.host?.phone,
      pricingData?.full_day_rate || pricingData?.charterRate,
      yacht.insurance?.has_insurance || yacht.insurance?.provider,
      yacht.insurance?.expiry_date || yacht.insurance?.policyNumber
    ];

    total += essentialFields.length;
    completed += essentialFields.filter(f => f !== null && f !== undefined && f !== '').length;

    // Photos
    total += 1;
    if (yacht.photos?.count > 0) completed += 1;

    return Math.round((completed / total) * 100);
  };

  const completion = calculateCompletion();
  const isIncomplete = completion < 100;

  return (
    <>
      <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <SailingOutlined />
              </Avatar>
              <Box>
                <Typography variant="h6">
                  {yacht.yacht?.name || 'Unnamed Yacht'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {yacht.yacht?.manufacturer} {yacht.yacht?.model} • {((yacht.yacht as any)?.yacht_type || yacht.yacht?.type)?.replace('_', ' ')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {yacht.yacht?.length}ft • {(yacht.yacht as any)?.year || yacht.yacht?.yearBuilt} • {(yacht.yacht as any)?.registration_number || yacht.yacht?.registrationNumber}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'right' }}>
              <Chip
                label={
                  yacht.status === 'approved'
                    ? 'Approved'
                    : yacht.status === 'rejected'
                    ? 'Rejected'
                    : yacht.status === 'draft'
                    ? 'Incomplete'
                    : 'Pending Approval'
                }
                color={
                  yacht.status === 'approved'
                    ? 'success'
                    : yacht.status === 'rejected'
                    ? 'error'
                    : yacht.status === 'draft'
                    ? 'warning'
                    : 'info'
                }
                size="small"
                sx={{ mb: 1 }}
              />
              {isIncomplete && (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={completion}
                    sx={{ width: 120, mb: 0.5 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {completion}% complete
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Quick Stats Grid */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Host
              </Typography>
              <Typography variant="body2">{yacht.host?.name || 'Unknown'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Guest Capacity
              </Typography>
              <Typography variant="body2">{(yacht.yacht as any)?.guests ?? (yacht.yacht as any)?.guest_capacity ?? yacht.yacht?.guestCapacity ?? 0}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Insurance
              </Typography>
              <Typography variant="body2">{yacht.insurance?.verified ? 'Verified' : 'Comprehensive'}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Photos
              </Typography>
              <Typography variant="body2">{yacht.photos.count} uploaded</Typography>
            </Grid>
          </Grid>

          {/* Yacht Details Section */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Yacht Information
              </Typography>
              {!editingDetails && (
                <IconButton size="small" onClick={() => setEditingDetails(true)}>
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Box>

            {editingDetails ? (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Registration Number"
                    value={detailsForm.registrationNumber}
                    onChange={(e) => setDetailsForm({ ...detailsForm, registrationNumber: e.target.value })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Home Port"
                    value={detailsForm.homePort}
                    onChange={(e) => setDetailsForm({ ...detailsForm, homePort: e.target.value })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="State"
                    value={detailsForm.state}
                    onChange={(e) => setDetailsForm({ ...detailsForm, state: e.target.value })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="City"
                    value={detailsForm.city}
                    onChange={(e) => setDetailsForm({ ...detailsForm, city: e.target.value })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Year Built"
                    value={detailsForm.yearBuilt}
                    onChange={(e) => setDetailsForm({ ...detailsForm, yearBuilt: e.target.value })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Guest Capacity"
                    type="number"
                    value={detailsForm.guestCapacity}
                    onChange={(e) => setDetailsForm({ ...detailsForm, guestCapacity: parseInt(e.target.value) })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Crew Capacity"
                    type="number"
                    value={detailsForm.crewCapacity}
                    onChange={(e) => setDetailsForm({ ...detailsForm, crewCapacity: parseInt(e.target.value) })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Cabins"
                    type="number"
                    value={detailsForm.cabins}
                    onChange={(e) => setDetailsForm({ ...detailsForm, cabins: parseInt(e.target.value) })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Bathrooms"
                    type="number"
                    value={detailsForm.bathrooms}
                    onChange={(e) => setDetailsForm({ ...detailsForm, bathrooms: parseInt(e.target.value) })}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setEditingDetails(false)}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={handleSaveDetails}>
                      Save Changes
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SailingOutlined fontSize="small" color="action" />
                    <Typography variant="body2">
                      {(yacht.yacht as any)?.registration_number || yacht.yacht?.registrationNumber || 'Not set'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    {(() => {
                      const yachtData = yacht.yacht as any;
                      const homePort = yachtData?.homeport || yachtData?.home_port || yacht.yacht?.homePort || yachtData?.marina;
                      const city = yacht.yacht?.city;
                      const state = yacht.yacht?.state;

                      const parts = [];
                      if (homePort) parts.push(homePort);
                      if (city) parts.push(city);
                      if (state) parts.push(state);

                      return parts.length > 0 ? parts.join(' • ') : 'Not set';
                    })()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Built: {(yacht.yacht as any)?.year || yacht.yacht?.yearBuilt || 'Unknown'} | Guests: {(yacht.yacht as any)?.guests ?? (yacht.yacht as any)?.guest_capacity ?? yacht.yacht?.guestCapacity ?? 0} | Crew: {(yacht.yacht as any)?.crew ?? (yacht.yacht as any)?.crew_capacity ?? yacht.yacht?.crewCapacity ?? 0} | Cabins: {(yacht.yacht as any)?.bedrooms ?? yacht.yacht?.cabins ?? 0} | Bathrooms: {yacht.yacht?.bathrooms ?? 0}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Pricing Information */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Charter Rates
              </Typography>
              {!editingPricing && (
                <IconButton size="small" onClick={() => setEditingPricing(true)}>
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Box>

            {editingPricing ? (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Daily Charter Rate"
                    type="number"
                    value={pricingForm.charterRate}
                    onChange={(e) => setPricingForm({ ...pricingForm, charterRate: parseFloat(e.target.value) })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Weekly Rate (optional)"
                    type="number"
                    value={pricingForm.weeklyRate || ''}
                    onChange={(e) => setPricingForm({ ...pricingForm, weeklyRate: e.target.value ? parseFloat(e.target.value) : null })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Captain Fee (optional)"
                    type="number"
                    value={pricingForm.captainFee || ''}
                    onChange={(e) => setPricingForm({ ...pricingForm, captainFee: e.target.value ? parseFloat(e.target.value) : null })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Crew Fee (optional)"
                    type="number"
                    value={pricingForm.crewFee || ''}
                    onChange={(e) => setPricingForm({ ...pricingForm, crewFee: e.target.value ? parseFloat(e.target.value) : null })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Fuel Surcharge"
                    type="number"
                    value={pricingForm.fuelSurcharge || ''}
                    onChange={(e) => setPricingForm({ ...pricingForm, fuelSurcharge: e.target.value ? parseFloat(e.target.value) : null })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Cleaning Fee"
                    type="number"
                    value={pricingForm.cleaningFee || ''}
                    onChange={(e) => setPricingForm({ ...pricingForm, cleaningFee: e.target.value ? parseFloat(e.target.value) : null })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Security Deposit"
                    type="number"
                    value={pricingForm.securityDeposit || ''}
                    onChange={(e) => setPricingForm({ ...pricingForm, securityDeposit: e.target.value ? parseFloat(e.target.value) : null })}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getCurrencySymbol(pricingForm.currency)}</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={pricingForm.currency}
                      onChange={(e) => setPricingForm({ ...pricingForm, currency: e.target.value })}
                      label="Currency"
                    >
                      <MenuItem value="NGN">NGN - Nigerian Naira</MenuItem>
                      <MenuItem value="USD">USD - US Dollar</MenuItem>
                      <MenuItem value="EUR">EUR - Euro</MenuItem>
                      <MenuItem value="GBP">GBP - British Pound</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setEditingPricing(false)}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" onClick={handleSavePricing}>
                      Save Changes
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoney fontSize="small" color="action" />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getCurrencySymbol(yacht.pricing?.currency || 'NGN')}{((yacht.pricing as any)?.full_day_rate || yacht.pricing?.charterRate)?.toLocaleString() || 'Not set'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">/ day</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    Weekly: {((yacht.pricing as any)?.weekly_rate || yacht.pricing?.weeklyRate) ? `${getCurrencySymbol(yacht.pricing.currency)}${((yacht.pricing as any)?.weekly_rate || yacht.pricing?.weeklyRate).toLocaleString()}` : 'Not set'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Captain: {((yacht.pricing as any)?.captain_fee || yacht.pricing?.captainFee) ? `${getCurrencySymbol(yacht.pricing.currency)}${((yacht.pricing as any)?.captain_fee || yacht.pricing?.captainFee).toLocaleString()}` : 'Included'} |
                    Crew: {((yacht.pricing as any)?.crew_fee || yacht.pricing?.crewFee) ? `${getCurrencySymbol(yacht.pricing.currency)}${((yacht.pricing as any)?.crew_fee || yacht.pricing?.crewFee).toLocaleString()}` : 'Included'} |
                    Fuel: {((yacht.pricing as any)?.fuel_surcharge || yacht.pricing?.fuelSurcharge) ? `${getCurrencySymbol(yacht.pricing.currency)}${((yacht.pricing as any)?.fuel_surcharge || yacht.pricing?.fuelSurcharge).toLocaleString()}` : 'N/A'} |
                    Cleaning: {((yacht.pricing as any)?.cleaning_fee || yacht.pricing?.cleaningFee) ? `${getCurrencySymbol(yacht.pricing.currency)}${((yacht.pricing as any)?.cleaning_fee || yacht.pricing?.cleaningFee).toLocaleString()}` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Yacht Destination Pricing Section */}
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'secondary.light', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Place sx={{ fontSize: 18, color: 'secondary.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Yacht Destination Pricing
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDestinationPricingDialog(true)}
              >
                Manage
              </Button>
            </Box>
          </Box>

          {/* Host Information */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              HOST INFORMATION
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={yacht.host?.profilePicture || undefined} sx={{ width: 32, height: 32 }}>
                <Person />
              </Avatar>
              <Box>
                <Typography variant="body2">{yacht.host?.name || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {yacht.host?.phone} {yacht.host?.email && `• ${yacht.host.email}`}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Insurance Information */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              INSURANCE
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security fontSize="small" color="action" />
              <Typography variant="body2">
                {yacht.insurance?.provider} - {yacht.insurance?.policyNumber}
              </Typography>
              {yacht.insurance?.verified && (
                <Chip label="Verified" size="small" color="success" icon={<VerifiedUser />} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Expires: {yacht.insurance?.expiryDate ? new Date(yacht.insurance.expiryDate).toLocaleDateString() : 'Unknown'}
            </Typography>
          </Box>

          {/* Photos Section */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                PHOTOS ({yacht.photos.count})
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Collections />}
                onClick={() => setImageManagementDialog(true)}
              >
                Manage Images
              </Button>
            </Box>
            {yacht.photos.items.length > 0 ? (
              <ImageList cols={4} gap={8} sx={{ maxHeight: 200 }}>
                {yacht.photos.items.slice(0, 8).map((photo, index) => (
                  <ImageListItem
                    key={photo.id}
                    sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    onClick={() => handleImageClick(index)}
                  >
                    <img
                      src={photo.url}
                      alt={`Yacht photo ${index + 1}`}
                      loading="lazy"
                      style={{ height: 100, objectFit: 'cover', borderRadius: 4 }}
                    />
                    {photo.isPrimary && (
                      <Chip
                        label="Primary"
                        size="small"
                        color="primary"
                        sx={{ position: 'absolute', top: 4, left: 4 }}
                      />
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No photos uploaded yet
              </Typography>
            )}
          </Box>

          {/* Features/Amenities */}
          {yacht.amenities && yacht.amenities.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                AMENITIES
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {yacht.amenities.map((amenity) => (
                  <Chip key={amenity} label={amenity} size="small" variant="outlined" />
                ))}
              </Box>
            </Box>
          )}

          {/* Instant Booking Badge */}
          {yacht.instantBooking && (
            <Chip
              label="Instant Booking"
              size="small"
              color="success"
              icon={<CheckCircle />}
              sx={{ mb: 2 }}
            />
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => setDetailsDialog(true)}
            >
              View Details & Photos
            </Button>

            {yacht.status === 'approved' && (
              <Button
                variant="outlined"
                startIcon={localIsActive ? <VisibilityOff /> : <Visibility />}
                onClick={() => setToggleActiveDialog({ open: true, toggling: false })}
                color={localIsActive ? 'warning' : 'success'}
              >
                {localIsActive ? 'Hide from Renters' : 'Show to Renters'}
              </Button>
            )}

            {yacht.status === 'pending_approval' && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setApproveDialog({ open: true, notes: '' })}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => setRejectDialog({ open: true, reason: '' })}
                >
                  Reject
                </Button>
              </>
            )}
          </Box>

          {/* Revert to Pending Button (for approved/rejected yachts) */}
          {(yacht.status === 'approved' || yacht.status === 'rejected') && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<RestartAlt />}
                onClick={() => {
                  setRevertDialog({ open: true, notes: '' });
                }}
              >
                Revert to Pending
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                Undo {yacht.status === 'approved' ? 'approval' : 'rejection'} and return to pending review
              </Typography>
            </Box>
          )}

          {/* Submission Date */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Submitted: {new Date(yacht.createdAt).toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false, notes: '' })}>
        <DialogTitle>Approve Yacht Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to approve this yacht? It will become available for booking.
          </Typography>
          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            fullWidth
            value={approveDialog.notes}
            onChange={(e) => setApproveDialog({ ...approveDialog, notes: e.target.value })}
            placeholder="Add any notes about this approval..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog({ open: false, notes: '' })}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleApprove}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, reason: '' })}>
        <DialogTitle>Reject Yacht Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this yacht. The host will be notified.
          </Typography>
          <TextField
            label="Rejection Reason"
            multiline
            rows={4}
            fullWidth
            required
            value={rejectDialog.reason}
            onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
            placeholder="E.g., Insufficient documentation, Photos quality too low, Insurance expired..."
            error={rejectDialog.reason.trim() === ''}
            helperText={rejectDialog.reason.trim() === '' ? 'Reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, reason: '' })}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={!rejectDialog.reason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert to Pending Dialog */}
      <Dialog open={revertDialog.open} onClose={() => setRevertDialog({ open: false, notes: '' })}>
        <DialogTitle>Revert Yacht to Pending Review</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will revert the yacht back to pending status, allowing you to re-review and make a new decision.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current status: <strong>{yacht.status === 'approved' ? 'Approved' : 'Rejected'}</strong>
          </Typography>
          <TextField
            label="Notes (Optional)"
            multiline
            rows={3}
            fullWidth
            value={revertDialog.notes}
            onChange={(e) => setRevertDialog({ ...revertDialog, notes: e.target.value })}
            placeholder="Add any notes about why you're reverting this decision..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertDialog({ open: false, notes: '' })}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRevert}
          >
            Revert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toggle Active Dialog */}
      <Dialog open={toggleActiveDialog.open} onClose={() => !toggleActiveDialog.toggling && setToggleActiveDialog({ open: false, toggling: false })}>
        <DialogTitle>{localIsActive ? 'Hide Yacht' : 'Show Yacht'}</DialogTitle>
        <DialogContent>
          <Typography>
            {localIsActive
              ? 'Hide this yacht from renters? It will no longer appear in search results or be available for booking.'
              : 'Show this yacht to renters? It will appear in search results and be available for booking.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleActiveDialog({ open: false, toggling: false })} disabled={toggleActiveDialog.toggling}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={localIsActive ? 'warning' : 'success'}
            onClick={handleToggleActive}
            disabled={toggleActiveDialog.toggling}
          >
            {toggleActiveDialog.toggling ? 'Processing...' : localIsActive ? 'Hide' : 'Show'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageViewer.open}
        onClose={() => setImageViewer({ ...imageViewer, open: false })}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.95)' }
        }}
      >
        <DialogTitle sx={{ color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography>
            Yacht Photos ({imageViewer.currentIndex + 1} / {yacht.photos.items.length})
          </Typography>
          <IconButton
            onClick={() => setImageViewer({ ...imageViewer, open: false })}
            sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ position: 'relative', minHeight: 500 }}>
          {yacht.photos.items.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                position: 'relative'
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setMagnifierPosition({ x: 0, y: 0 })}
            >
              <Box sx={{ display: 'flex', gap: 1, position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
                <Tooltip title="Zoom Out">
                  <IconButton
                    onClick={() => handleZoom(-0.25)}
                    disabled={imageViewer.zoom <= 0.5}
                    sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <ZoomOut />
                  </IconButton>
                </Tooltip>
                <Chip label={`${Math.round(imageViewer.zoom * 100)}%`} sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)' }} />
                <Tooltip title="Zoom In">
                  <IconButton
                    onClick={() => handleZoom(0.25)}
                    disabled={imageViewer.zoom >= 5}
                    sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <ZoomIn />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reset Zoom">
                  <IconButton
                    onClick={() => handleZoom(1 - imageViewer.zoom)}
                    sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.5)', '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' } }}
                  >
                    <RestartAlt />
                  </IconButton>
                </Tooltip>
              </Box>

              <IconButton
                onClick={handlePrevImage}
                sx={{
                  position: 'absolute',
                  left: 10,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2
                }}
              >
                <NavigateBefore />
              </IconButton>

              <img
                ref={imageRef}
                src={yacht.photos.items[imageViewer.currentIndex].url}
                alt={`Yacht ${imageViewer.currentIndex + 1}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  transform: `scale(${imageViewer.zoom})`,
                  transition: 'transform 0.2s'
                }}
              />

              <IconButton
                onClick={handleNextImage}
                sx={{
                  position: 'absolute',
                  right: 10,
                  color: 'white',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' },
                  zIndex: 2
                }}
              >
                <NavigateNext />
              </IconButton>

              {imageViewer.showMagnifier && imageRef.current && (
                <Box
                  sx={{
                    position: 'absolute',
                    width: 200,
                    height: 200,
                    border: '3px solid white',
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    left: `calc(${magnifierPosition.x}% - 100px)`,
                    top: `calc(${magnifierPosition.y}% - 100px)`,
                    backgroundImage: `url(${yacht.photos.items[imageViewer.currentIndex].url})`,
                    backgroundSize: `${imageRef.current.width * 2.5}px ${imageRef.current.height * 2.5}px`,
                    backgroundPosition: `${-magnifierPosition.x * imageRef.current.width * 2.5 / 100 + 100}px ${-magnifierPosition.y * imageRef.current.height * 2.5 / 100 + 100}px`,
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Yacht Image Management Dialog */}
      <YachtImageManagementDialog
        open={imageManagementDialog}
        onClose={() => setImageManagementDialog(false)}
        yachtId={yacht.id}
        yachtName={yacht.yacht?.name || 'Unnamed Yacht'}
      />

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Yacht Details</DialogTitle>
        <DialogContent>
          <List>
            <ListItem>
              <ListItemText
                primary="Yacht Name"
                secondary={yacht.yacht?.name || 'N/A'}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Manufacturer & Model"
                secondary={`${yacht.yacht?.manufacturer} ${yacht.yacht?.model}`}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Specifications"
                secondary={`Length: ${yacht.yacht?.length}ft | Beam: ${yacht.yacht?.beam}ft | Draft: ${yacht.yacht?.draft}ft`}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Capacity"
                secondary={`Guests: ${yacht.yacht?.guestCapacity} | Crew: ${yacht.yacht?.crewCapacity} | Cabins: ${yacht.yacht?.cabins} | Bathrooms: ${yacht.yacht?.bathrooms || 0}`}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Registration"
                secondary={`${yacht.yacht?.registrationNumber} | Flag: ${yacht.yacht?.flag}`}
              />
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Host"
                secondary={`${yacht.host?.name} | ${yacht.host?.phone} ${yacht.host?.email ? `| ${yacht.host.email}` : ''}`}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Yacht Destination Pricing Dialog */}
      <ManageYachtPricingDialog
        open={destinationPricingDialog}
        yacht={{
          yachtId: yacht.id,
          yachtName: yacht.yacht?.name || 'Unknown',
          yachtType: (yacht.yacht as any)?.yacht_type || yacht.yacht?.type || '',
          capacity: (yacht.yacht as any)?.guests || yacht.yacht?.guestCapacity || 0,
          ownerId: yacht.host?.id || '',
          ownerName: yacht.host?.name,
          city: (yacht.yacht as any)?.city || undefined,
          country: (yacht.yacht as any)?.country || undefined,
          homeport: (yacht.yacht as any)?.homeport || undefined,
          destinationPricingCount: 0,
          hasDestinationPricing: false,
          pricings: [],
        }}
        onClose={() => setDestinationPricingDialog(false)}
        onSuccess={() => {
          setDestinationPricingDialog(false);
          toast.success('Destination pricing updated successfully');
          onPricingUpdate?.();
        }}
      />
    </>
  );
};
